/**
 * LayoutManagerControl Component
 *
 * Compound header control (name button + chevron) with layout manager
 * popover and modal orchestration. Lives in the TradingHub header's
 * actionMenuSlot during live-trading phase.
 *
 * Architecture follows FeaturesDropdown pattern:
 * - Popover with layout list, create new, per-row overflow menus
 * - Modals mounted OUTSIDE the Popover to survive popover teardown
 * - All state localized within this component
 *
 * @see LayoutNameModal.tsx for create/duplicate name input modal
 * @see RenameLayoutModal.tsx for rename modal
 * @see DeleteLayoutModal.tsx for delete confirmation modal
 */

// 1. External dependencies
import React, { useState, useMemo, useCallback } from 'react';
import { ChevronDown, Check, EllipsisVertical, Copy, Pencil, Trash2 } from 'lucide-react';

// 2. Internal absolute imports
import {
  Button,
  Popover, PopoverTrigger, PopoverContent,
  Dropdown, DropdownTrigger, DropdownMenu, DropdownItem,
  Divider, ScrollShadow, Switch,
  useToastNotification,
} from '@enfomentis/ui';

// 3. Type imports
import type { SavedLayout } from '@/types/shared';

// 4. Relative imports
import { LayoutNameModal } from './LayoutNameModal';
import { RenameLayoutModal } from './RenameLayoutModal';
import { DeleteLayoutModal } from './DeleteLayoutModal';
import { LayoutSwitchDialog } from './LayoutSwitchDialog';

// ===== TYPES =====

/** Config object that drives LayoutNameModal for both create and duplicate flows */
interface NameModalConfig {
  title: string;
  defaultName?: string;
  confirmLabel?: string;
  onConfirm: (name: string) => Promise<void>;
}

export interface LayoutManagerControlProps {
  savedLayouts: SavedLayout[];
  activeLayoutId: string | undefined;
  autosaveEnabled: boolean;
  isDirty: boolean;
  createSavedLayout: (name: string) => Promise<void>;
  duplicateSavedLayoutById: (layoutId: string, newName: string) => Promise<void>;
  renameSavedLayout: (layoutId: string, newName: string) => Promise<void>;
  deleteSavedLayout: (layoutId: string) => Promise<void>;
  applySavedLayout: (layoutId: string) => Promise<void>;
  setAutosaveEnabled: (enabled: boolean) => Promise<void>;
  saveToActiveLayout: () => Promise<void>;
}

// ===== COMPONENT =====

export const LayoutManagerControl: React.FC<LayoutManagerControlProps> = React.memo(({
  savedLayouts,
  activeLayoutId,
  autosaveEnabled,
  isDirty,
  createSavedLayout,
  duplicateSavedLayoutById,
  renameSavedLayout,
  deleteSavedLayout,
  applySavedLayout,
  setAutosaveEnabled,
  saveToActiveLayout,
}) => {
  // ---------------------------------------------------------------------------
  // Internal state
  // ---------------------------------------------------------------------------

  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [nameModalConfig, setNameModalConfig] = useState<NameModalConfig | null>(null);
  const [renameTarget, setRenameTarget] = useState<{ layoutId: string; currentName: string } | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<{ layoutId: string; layoutName: string } | null>(null);
  const [isBusy, setIsBusy] = useState(false);
  const [pendingSwitchLayoutId, setPendingSwitchLayoutId] = useState<string | null>(null);

  const { showToast } = useToastNotification();

  // ---------------------------------------------------------------------------
  // Derived state
  // ---------------------------------------------------------------------------

  const activeLayout = useMemo(
    () => savedLayouts.find((l) => l.id === activeLayoutId),
    [savedLayouts, activeLayoutId],
  );

  const activeLayoutName = activeLayout?.name ?? '\u2014';

  const sortedLayouts = useMemo(
    () => [...savedLayouts].sort((a, b) => a.createdAt.localeCompare(b.createdAt)),
    [savedLayouts],
  );

  const canSave = isDirty && !autosaveEnabled && !!activeLayoutId && !isBusy;

  const pendingSwitchLayoutName = savedLayouts.find((l) => l.id === pendingSwitchLayoutId)?.name ?? '';

  // ---------------------------------------------------------------------------
  // Explicit save & autosave toggle handlers
  // ---------------------------------------------------------------------------

  const handleExplicitSave = useCallback(async (): Promise<void> => {
    setIsBusy(true);
    try {
      await saveToActiveLayout();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to save layout';
      showToast({ color: 'danger', title: 'Error', description: message });
    } finally {
      setIsBusy(false);
    }
  }, [saveToActiveLayout, showToast]);

  const handleAutosaveToggle = useCallback(async (value: boolean): Promise<void> => {
    setIsBusy(true);
    try {
      await setAutosaveEnabled(value);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to toggle autosave';
      showToast({ color: 'danger', title: 'Error', description: message });
    } finally {
      setIsBusy(false);
    }
  }, [setAutosaveEnabled, showToast]);

  // ---------------------------------------------------------------------------
  // Popover action handlers
  // ---------------------------------------------------------------------------

  const handleCreateNew = useCallback((): void => {
    setIsPopoverOpen(false);
    setNameModalConfig({
      title: 'Create Layout',
      onConfirm: createSavedLayout,
    });
  }, [createSavedLayout]);

  const handleApplyLayout = useCallback(async (layoutId: string): Promise<void> => {
    if (isBusy) return;
    if (layoutId === activeLayoutId) return;
    setIsPopoverOpen(false);
    if (isDirty && !autosaveEnabled) {
      setPendingSwitchLayoutId(layoutId);
      return;
    }
    try {
      await applySavedLayout(layoutId);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to apply layout';
      showToast({ color: 'danger', title: 'Error', description: message });
    }
  }, [isBusy, activeLayoutId, isDirty, autosaveEnabled, applySavedLayout, showToast]);

  // ---------------------------------------------------------------------------
  // Layout switch dialog handlers
  // ---------------------------------------------------------------------------

  const handleSwitchSave = useCallback(async (): Promise<void> => {
    if (!pendingSwitchLayoutId) return;
    await saveToActiveLayout();
    await applySavedLayout(pendingSwitchLayoutId);
  }, [saveToActiveLayout, applySavedLayout, pendingSwitchLayoutId]);

  const handleSwitchDiscard = useCallback(async (): Promise<void> => {
    if (!pendingSwitchLayoutId) return;
    await applySavedLayout(pendingSwitchLayoutId);
  }, [applySavedLayout, pendingSwitchLayoutId]);

  const handleSwitchCancel = useCallback((): void => {
    setPendingSwitchLayoutId(null);
  }, []);

  // ---------------------------------------------------------------------------
  // Overflow menu action handlers
  // ---------------------------------------------------------------------------

  const handleDuplicate = useCallback((layout: SavedLayout): void => {
    setIsPopoverOpen(false);
    setNameModalConfig({
      title: 'Duplicate Layout',
      defaultName: `Copy of ${layout.name}`,
      confirmLabel: 'Duplicate',
      onConfirm: (name: string) => duplicateSavedLayoutById(layout.id, name),
    });
  }, [duplicateSavedLayoutById]);

  const handleRename = useCallback((layout: SavedLayout): void => {
    setIsPopoverOpen(false);
    setRenameTarget({ layoutId: layout.id, currentName: layout.name });
  }, []);

  const handleDelete = useCallback((layout: SavedLayout): void => {
    setIsPopoverOpen(false);
    setDeleteTarget({ layoutId: layout.id, layoutName: layout.name });
  }, []);

  // ---------------------------------------------------------------------------
  // Modal close handlers
  // ---------------------------------------------------------------------------

  const closeNameModal = useCallback((): void => {
    setNameModalConfig(null);
  }, []);

  const closeRenameModal = useCallback((): void => {
    setRenameTarget(null);
  }, []);

  const closeDeleteModal = useCallback((): void => {
    setDeleteTarget(null);
  }, []);

  // ---------------------------------------------------------------------------
  // Modal confirm callbacks (stable via useCallback)
  // ---------------------------------------------------------------------------

  const handleRenameConfirm = useCallback(async (name: string): Promise<void> => {
    if (!renameTarget) return;
    await renameSavedLayout(renameTarget.layoutId, name);
  }, [renameTarget, renameSavedLayout]);

  const handleDeleteConfirm = useCallback(async (): Promise<void> => {
    if (!deleteTarget) return;

    // Capture fallback info before delete mutates state
    const isActive = deleteTarget.layoutId === activeLayoutId;
    const fallbackLayout = isActive
      ? sortedLayouts.find((l) => l.id !== deleteTarget.layoutId)
      : undefined;

    await deleteSavedLayout(deleteTarget.layoutId);

    // Auto-apply fallback if we deleted the active layout
    if (isActive && fallbackLayout) {
      try {
        await applySavedLayout(fallbackLayout.id);
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Failed to apply fallback layout';
        showToast({ color: 'danger', title: 'Error', description: message });
      }
    }
  }, [deleteTarget, activeLayoutId, sortedLayouts, deleteSavedLayout, applySavedLayout, showToast]);

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  // Shared name zone content — rendered inside both <button> and <span> branches
  const nameZoneContent = (
    <>
      <span className="truncate">{activeLayoutName}</span>
      {isDirty && (
        <>
          <span
            className="shrink-0 w-1.5 h-1.5 rounded-full bg-warning"
            aria-hidden="true"
          />
          <span className="sr-only">(unsaved changes)</span>
        </>
      )}
    </>
  );

  return (
    <>
      {/* Header control: name zone + chevron popover trigger */}
      <div className="flex items-center shrink-0">
        {/* Name zone — <button> when saveable, <span> when inert */}
        {canSave ? (
          <button
            type="button"
            className="flex items-center gap-1.5 pl-2 pr-1 py-1 text-xs font-medium max-w-32 rounded-md hover:bg-default-100 active:bg-default-200 transition-colors cursor-pointer"
            onClick={handleExplicitSave}
            aria-label={`Save layout: ${activeLayoutName}`}
          >
            {nameZoneContent}
          </button>
        ) : (
          <span className="flex items-center gap-1.5 pl-2 pr-1 py-1 text-xs font-medium max-w-32 cursor-default">
            {nameZoneContent}
          </span>
        )}

        {/* Chevron — always opens the popover */}
        <Popover placement="bottom-end" isOpen={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
          <PopoverTrigger>
            <Button
              isIconOnly
              variant="light"
              size="sm"
              className="shrink-0"
              aria-label="Open layout manager"
            >
              <ChevronDown size={20} />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="p-2 min-w-56 max-w-64">
            <div className="flex flex-col gap-1.5 w-full">
              {/* Header */}
              <span className="text-[10px] font-semibold uppercase tracking-wider text-default-400">
                Layouts
              </span>

              {/* Saved layouts list */}
              <ScrollShadow className="max-h-52 flex flex-col gap-0.5">
                {sortedLayouts.map((layout) => {
                  const isActive = layout.id === activeLayoutId;
                  return (
                    <div key={layout.id} className="flex items-center gap-1 group">
                      {/* Clickable name area */}
                      <button
                        type="button"
                        className="flex items-center gap-2 flex-1 min-w-0 px-1.5 py-1 rounded-md text-left hover:bg-default-100 transition-colors cursor-pointer"
                        onClick={() => handleApplyLayout(layout.id)}
                      >
                        <span className="w-4 shrink-0 flex items-center justify-center">
                          {isActive && <Check size={14} className="text-primary" />}
                        </span>
                        <span className={`text-sm truncate ${isActive ? 'font-medium text-foreground' : 'text-default-700'}`}>
                          {layout.name}
                        </span>
                      </button>

                      {/* Overflow menu trigger — sibling of clickable area, not nested */}
                      <Dropdown placement="bottom-end">
                        <DropdownTrigger>
                          <Button
                            isIconOnly
                            size="sm"
                            variant="light"
                            isDisabled={isBusy}
                            className="shrink-0 md:opacity-0 md:group-hover:opacity-100 focus-visible:opacity-100 transition-opacity"
                            aria-label={`Actions for ${layout.name}`}
                          >
                            <EllipsisVertical size={14} />
                          </Button>
                        </DropdownTrigger>
                        <DropdownMenu aria-label={`Actions for ${layout.name}`}>
                          {[
                            <DropdownItem
                              key="duplicate"
                              startContent={<Copy size={14} />}
                              onPress={() => handleDuplicate(layout)}
                            >
                              Make a copy
                            </DropdownItem>,
                            <DropdownItem
                              key="rename"
                              startContent={<Pencil size={14} />}
                              onPress={() => handleRename(layout)}
                            >
                              Rename
                            </DropdownItem>,
                            ...(savedLayouts.length > 1 ? [
                              <DropdownItem
                                key="delete"
                                startContent={<Trash2 size={14} />}
                                className="text-danger"
                                color="danger"
                                onPress={() => handleDelete(layout)}
                              >
                                Delete
                              </DropdownItem>,
                            ] : []),
                          ]}
                        </DropdownMenu>
                      </Dropdown>
                    </div>
                  );
                })}
              </ScrollShadow>

              <Divider className="my-0.5" />

              {/* Autosave toggle */}
              <div className="flex items-center justify-between px-1.5 py-1">
                <span className="text-[10px] font-semibold uppercase tracking-wider text-default-400">
                  Autosave
                </span>
                <Switch
                  size="sm"
                  isSelected={autosaveEnabled}
                  onValueChange={handleAutosaveToggle}
                  isDisabled={isBusy}
                  aria-label="Toggle autosave"
                />
              </div>

              <Divider className="my-0.5" />

              {/* Create New */}
              <Button
                size="sm"
                variant="flat"
                color="primary"
                className="w-full"
                isDisabled={isBusy}
                onPress={handleCreateNew}
              >
                Create New
              </Button>
            </div>
          </PopoverContent>
        </Popover>
      </div>

      {/* Modals mounted OUTSIDE Popover to survive popover teardown */}
      <LayoutNameModal
        isOpen={nameModalConfig !== null}
        onClose={closeNameModal}
        onConfirm={nameModalConfig?.onConfirm ?? (async () => {})}
        title={nameModalConfig?.title ?? ''}
        {...(nameModalConfig?.defaultName !== undefined ? { defaultName: nameModalConfig.defaultName } : {})}
        {...(nameModalConfig?.confirmLabel !== undefined ? { confirmLabel: nameModalConfig.confirmLabel } : {})}
      />
      <RenameLayoutModal
        isOpen={renameTarget !== null}
        onClose={closeRenameModal}
        onConfirm={handleRenameConfirm}
        currentName={renameTarget?.currentName ?? ''}
      />
      <DeleteLayoutModal
        isOpen={deleteTarget !== null}
        onClose={closeDeleteModal}
        onConfirm={handleDeleteConfirm}
        layoutName={deleteTarget?.layoutName ?? ''}
      />
      <LayoutSwitchDialog
        isOpen={pendingSwitchLayoutId !== null}
        onClose={handleSwitchCancel}
        onSave={handleSwitchSave}
        onDiscard={handleSwitchDiscard}
        targetLayoutName={pendingSwitchLayoutName}
      />
    </>
  );
});

LayoutManagerControl.displayName = 'LayoutManagerControl';
